import { useState, useMemo, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import moment from 'moment';
import { message } from 'antd';

import { BookingJourney } from 'common/constants/booking';
import {
  QUERY_BOOKINGS_STATISTICS,
  QUERY_LANDLORD_ORDERS,
  QUERY_LANDLORD_BOOKINGS,
  QUERY_INSTANT_ORDERS,
  QUERY_LEADGEN_ORDERS,
} from '../queries';

export const orderAll = 'All';

export const opportunityStages = {
  COMPLETED: 'COMPLETED',
  CANCELLATION: 'CANCELLATION',
};

const defaultOpportunityStages = [opportunityStages.COMPLETED, opportunityStages.CANCELLATION];

const defaultOtherTableFilter = {
  pageNumber: 1,
  pageSize: 10,
  sortBy: 'MOVE_IN_DATE',
};

const createdAfter = moment().subtract(6, 'days').format('YYYY-MM-DD');

const useTableFilters = () => {
  const [bookingType, setBookingType] = useState(BookingJourney.SEMI_AUTOMATIC);
  const [bookingStage, setBookingStage] = useState(orderAll);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [otherTableFilter, setOtherTableFilter] = useState(defaultOtherTableFilter);

  const isHerculesBooking = useMemo(
    () => bookingType === BookingJourney.SEMI_AUTOMATIC,
    [bookingType],
  );

  const isLeadBooking = useMemo(
    () => bookingType === BookingJourney.LEAD_GENERATION,
    [bookingType],
  );

  const isInstantBooking = useMemo(
    () => bookingType === BookingJourney.INSTANT_BOOK,
    [bookingType],
  );

  const [queryBookingStatistics, { loading: statisticsLoading, data: statisticsData }] =
    useLazyQuery(QUERY_BOOKINGS_STATISTICS, {
      fetchPolicy: 'no-cache',
      variables: {
        createdAfter,
      },
      onCompleted: data => {
        const hasHerculesOrder = data?.viewer?.company?.herculesAllOrders?.totalCount > 0;
        const hasManualOrder = data?.viewer?.company?.manualAllBookings?.totalCount > 0;
        const hasInstantOrder = data?.viewer?.company?.instantAllOrders?.totalCount > 0;
        const hasLeadOrder = data?.viewer?.company?.leadGenAllOrders?.totalCount > 0;

        if (isHerculesBooking && !hasHerculesOrder && !hasInstantOrder) {
          setBookingType(BookingJourney.MANUAL);
        }

        if (hasInstantOrder && !hasHerculesOrder) {
          setBookingType(BookingJourney.INSTANT_BOOK);
        }

        if (!isHerculesBooking && !hasManualOrder && !hasInstantOrder) {
          setBookingType(BookingJourney.SEMI_AUTOMATIC);
        }

        if (!hasManualOrder && !hasInstantOrder && hasLeadOrder) {
          setBookingType(BookingJourney.LEAD_GENERATION);
        }
      },
    });

  const variables = useMemo(() => {
    const queryArguments = {
      ...otherTableFilter,
    };

    if (selectedProperties?.length > 0) {
      queryArguments.propertyIds = selectedProperties;
    }

    if (isHerculesBooking) {
      queryArguments.stages = bookingStage !== orderAll ? [bookingStage] : undefined;
      queryArguments.createdAfter = createdAfter;
    } else if (isInstantBooking) {
      queryArguments.stages = bookingStage !== orderAll ? [bookingStage] : undefined;
    } else if (isLeadBooking) {
      queryArguments.stages = undefined;
    } else {
      queryArguments.opportunityStages =
        bookingStage !== orderAll ? [bookingStage] : defaultOpportunityStages;
    }

    return queryArguments;
  }, [bookingStage, isHerculesBooking, isInstantBooking, otherTableFilter, selectedProperties]);
  //  leadProperty;
  //  LeadGenAllOrders;
  const [
    queryLandlordOrders,
    { data: orders, loading: queryOrdersLoading, refetch: refetchLandlordOrders },
  ] = useLazyQuery(QUERY_LANDLORD_ORDERS, {
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e.toString());
    },
  });

  const [
    queryInstantOrders,
    { data: instantOrders, loading: queryInstantLoading, refetch: refetchInstantdOrders },
  ] = useLazyQuery(QUERY_INSTANT_ORDERS, {
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e.toString());
    },
  });

  const [
    queryLeadOrders,
    { data: leadOrders, loading: leadOrdersLoading, refetch: refetchLeadOrdersOrders },
  ] = useLazyQuery(QUERY_LEADGEN_ORDERS, {
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e.toString());
    },
  });

  const [
    queryLandlordBookings,
    { data: bookings, loading: queryBookingsLoading, refetch: refetchLandlordBookings },
  ] = useLazyQuery(QUERY_LANDLORD_BOOKINGS, {
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e.toString());
    },
  });

  const pendingOrderCount = useMemo(() => {
    const { pendingConfirmationOrderCount, pendingRefundOrderCount } =
      orders?.viewer?.company ?? {};

    return {
      confirmation: pendingConfirmationOrderCount?.totalCount,
      refund: pendingRefundOrderCount?.totalCount,
    };
  }, [orders?.viewer?.company]);

  const propertyNameOptions = useMemo(
    () =>
      (statisticsData?.viewer?.allProperties?.edges ?? []).map(item => ({
        value: item.node.id,
        label: item.node.name,
      })),
    [statisticsData],
  );

  const bookingTableData = useMemo(() => {
    if (isHerculesBooking) {
      return orders;
    } else if (isInstantBooking) {
      return instantOrders;
    } else if (isLeadBooking) {
      return leadOrders;
    } else {
      return bookings;
    }
  }, [
    isHerculesBooking,
    orders,
    isInstantBooking,
    instantOrders,
    leadOrders,
    isLeadBooking,
    bookings,
  ]);

  const changeBookingType = value => {
    setBookingType(value);
    setBookingStage(orderAll);
    setOtherTableFilter(defaultOtherTableFilter);
  };

  const resetPageNumber = () => {
    setOtherTableFilter({ ...otherTableFilter, pageNumber: 1 });
  };

  const changeBookingStage = value => {
    setBookingStage(value ?? orderAll);
    resetPageNumber();
  };

  const changeSelectedProperties = value => {
    setSelectedProperties(value);
    resetPageNumber();
  };

  useEffect(() => {
    queryBookingStatistics({
      variables: {
        propertyIds: selectedProperties?.length > 0 ? selectedProperties : undefined,
      },
    });
  }, [queryBookingStatistics, selectedProperties]);

  useEffect(() => {
    if (isHerculesBooking) {
      queryLandlordOrders({ variables });
    } else if (isInstantBooking) {
      queryInstantOrders({ variables });
    } else if (isLeadBooking) {
      queryLeadOrders({ variables });
    } else {
      queryLandlordBookings({ variables });
    }
  }, [
    isHerculesBooking,
    isInstantBooking,
    queryLandlordBookings,
    queryLandlordOrders,
    isLeadBooking,
    queryInstantOrders,
    otherTableFilter,
    variables,
  ]);

  useEffect(() => {
    queryBookingStatistics();
  }, [queryBookingStatistics]);

  return {
    bookingType,
    setBookingType: changeBookingType,
    bookingStage,
    setBookingStage: changeBookingStage,
    otherTableFilter,
    setOtherTableFilter,
    selectedProperties,
    setSelectedProperties: changeSelectedProperties,
    statisticsLoading,
    statisticsData: statisticsData?.viewer ?? {},
    propertyNameOptions,
    pendingOrderCount,
    tableData: bookingTableData,
    tableLoading:
      queryOrdersLoading || queryBookingsLoading || queryInstantLoading || leadOrdersLoading,
    refetchTableData: isHerculesBooking ? refetchLandlordOrders : refetchLandlordBookings,
  };
};

export default useTableFilters;
